import { useState, useEffect, useRef } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, BookOpen, Lock, ChevronLeft, ChevronRight, Loader2, ZoomIn, ZoomOut, RotateCcw, Smartphone, Monitor } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Detect if mobile
const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

// Page turn animation - simpler for mobile
const pageVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }
  },
  exit: (direction) => ({
    x: direction > 0 ? '-100%' : '100%',
    opacity: 0,
    transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }
  })
};

export default function GuideReader() {
  const { isAuthenticated, loading: authLoading, token } = useAuth();
  const [guideInfo, setGuideInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageImage, setPageImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [error, setError] = useState(null);
  const [pageDirection, setPageDirection] = useState(0);
  const [mobile, setMobile] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showPageInput, setShowPageInput] = useState(false);
  const [pageInputValue, setPageInputValue] = useState('');
  
  // Touch/swipe handling
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const containerRef = useRef(null);
  const imageContainerRef = useRef(null);
  const pageImageRef = useRef(null); // Track previous image URL for cleanup
  const pageInputRef = useRef(null);

  // Check if mobile on mount
  useEffect(() => {
    setMobile(isMobile());
    const handleResize = () => setMobile(isMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch guide info
  useEffect(() => {
    const fetchGuideInfo = async () => {
      if (!token) return;
      
      try {
        const response = await fetch(`${API}/api/guide/info`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.status === 403) {
          setHasAccess(false);
          setLoading(false);
          return;
        }
        
        if (!response.ok) throw new Error('Failed to load guide');
        
        const data = await response.json();
        setGuideInfo(data);
        setHasAccess(true);
      } catch (err) {
        setError(err.message);
        toast.error('Failed to load guide');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && token) {
      fetchGuideInfo();
    }
  }, [isAuthenticated, token]);

  // Fetch page image - separate effect to avoid dependency loop
  useEffect(() => {
    if (!token || !hasAccess || !guideInfo) return;
    
    let isCancelled = false;
    
    const fetchPage = async () => {
      setPageLoading(true);
      try {
        const response = await fetch(`${API}/api/guide/page/${currentPage}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!response.ok) throw new Error('Failed to load page');
        if (isCancelled) return;
        
        const blob = await response.blob();
        if (isCancelled) return;
        
        const imageUrl = URL.createObjectURL(blob);
        
        // Cleanup previous URL
        if (pageImageRef.current) {
          URL.revokeObjectURL(pageImageRef.current);
        }
        pageImageRef.current = imageUrl;
        
        setPageImage(imageUrl);
      } catch (err) {
        if (!isCancelled) {
          toast.error('Failed to load page');
          
        }
      } finally {
        if (!isCancelled) {
          setPageLoading(false);
        }
      }
    };
    
    fetchPage();
    
    return () => {
      isCancelled = true;
    };
  }, [currentPage, hasAccess, guideInfo, token]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        goToNextPage();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevPage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, guideInfo]);

  // Navigation functions
  const goToNextPage = () => {
    if (currentPage < guideInfo?.total_pages) {
      setPageDirection(1);
      setCurrentPage(prev => prev + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setPageDirection(-1);
      setCurrentPage(prev => prev - 1);
    }
  };

  // Touch/Swipe handlers for mobile
  const handleTouchStart = (e) => {
    if (zoom > 1) {
      // When zoomed in, handle panning
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      });
    } else {
      touchStartX.current = e.touches[0].clientX;
    }
  };

  const handleTouchMove = (e) => {
    if (zoom > 1 && isDragging) {
      // Pan when zoomed in
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    } else {
      touchEndX.current = e.touches[0].clientX;
    }
  };

  const handleTouchEnd = () => {
    if (zoom > 1) {
      setIsDragging(false);
      return;
    }
    
    const swipeThreshold = 50;
    const diff = touchStartX.current - touchEndX.current;
    
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        goToNextPage(); // Swipe left = next page
      } else {
        goToPrevPage(); // Swipe right = prev page
      }
    }
  };

  // Zoom controls
  const zoomIn = () => {
    setZoom(prev => Math.min(prev + 0.5, 3));
  };

  const zoomOut = () => {
    setZoom(prev => {
      const newZoom = Math.max(prev - 0.5, 1);
      if (newZoom === 1) {
        setPosition({ x: 0, y: 0 }); // Reset position when fully zoomed out
      }
      return newZoom;
    });
  };

  const resetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  // Mouse drag for desktop zoom panning
  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (zoom > 1 && isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Double tap/click to zoom
  const handleDoubleClick = (e) => {
    if (zoom === 1) {
      setZoom(2);
      // Center zoom on click position
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      setPosition({ x: -x, y: -y });
    } else {
      resetZoom();
    }
  };

  // Reset zoom when changing pages
  useEffect(() => {
    resetZoom();
  }, [currentPage]);

  // Toggle controls on tap (mobile)
  const handleImageTap = () => {
    if (mobile && zoom === 1) {
      setShowControls(prev => !prev);
    }
  };

  // Loading states
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-black pt-28 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login?redirect=/guide/read" />;
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-black pt-28">
        <div className="max-w-xl mx-auto px-6 py-20 text-center">
          <Lock className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
          <h1 className="font-serif text-3xl text-white mb-4">Access Required</h1>
          <p className="text-[#999999] mb-8">
            Purchase The Signal: Upgrade to access this content.
          </p>
          <Link to="/products">
            <Button className="bg-[#D4AF37] hover:bg-[#C9A030] text-black">
              Get Access
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const progress = ((currentPage / (guideInfo?.total_pages || 1)) * 100).toFixed(0);

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-20 md:pt-24 pb-24 md:pb-8">
      {/* Header - Hidden on mobile when tapped */}
      <AnimatePresence>
        {(showControls || !mobile) && (
          <motion.header 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-16 md:top-20 left-0 right-0 z-40 bg-black/90 backdrop-blur-lg border-b border-[#1a1a1a]"
          >
            <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
              <Link to="/account" className="flex items-center gap-2 text-[#999999] hover:text-white transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Back</span>
              </Link>
              
              <div className="flex items-center gap-3">
                <BookOpen className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-white text-sm font-medium">The Signal</span>
              </div>
              
              <div className="text-sm text-[#999999]">
                {progress}% complete
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="h-0.5 bg-[#1a1a1a]">
              <motion.div 
                className="h-full bg-[#D4AF37]"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </motion.header>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div 
        ref={containerRef}
        className="relative max-w-4xl mx-auto px-2 sm:px-4 pt-16 md:pt-20"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Zoom Controls */}
        <AnimatePresence>
          {(showControls || !mobile) && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex items-center justify-center gap-2 mb-4"
            >
              <Button
                onClick={zoomOut}
                disabled={zoom <= 1}
                variant="outline"
                size="sm"
                className="border-[#333] text-white hover:bg-[#1a1a1a] disabled:opacity-30 h-8 w-8 p-0"
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-white text-sm min-w-[50px] text-center">{Math.round(zoom * 100)}%</span>
              <Button
                onClick={zoomIn}
                disabled={zoom >= 3}
                variant="outline"
                size="sm"
                className="border-[#333] text-white hover:bg-[#1a1a1a] disabled:opacity-30 h-8 w-8 p-0"
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
              {zoom > 1 && (
                <Button
                  onClick={resetZoom}
                  variant="outline"
                  size="sm"
                  className="border-[#333] text-white hover:bg-[#1a1a1a] h-8 w-8 p-0 ml-2"
                >
                  <RotateCcw className="w-4 h-4" />
                </Button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Page Container */}
        <div 
          ref={imageContainerRef}
          className="relative bg-[#111] rounded-lg shadow-2xl overflow-hidden"
          style={{ 
            minHeight: mobile ? '60vh' : '75vh',
            cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
          }}
          onClick={handleImageTap}
          onDoubleClick={handleDoubleClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <AnimatePresence mode="wait" custom={pageDirection}>
            {pageLoading ? (
              <motion.div 
                key="loader"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex items-center justify-center"
              >
                <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
              </motion.div>
            ) : pageImage ? (
              <motion.div
                key={currentPage}
                custom={pageDirection}
                variants={pageVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="w-full flex items-center justify-center"
              >
                <img 
                  src={pageImage} 
                  alt={`Page ${currentPage}`}
                  className="w-full h-auto select-none"
                  draggable="false"
                  style={{ 
                    transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                    transformOrigin: 'center center',
                    transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                    pointerEvents: zoom > 1 ? 'auto' : 'none',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                    cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                  }}
                />
              </motion.div>
            ) : (
              <div className="flex items-center justify-center h-[60vh] text-[#999999]">
                Loading...
              </div>
            )}
          </AnimatePresence>

          {/* Side Navigation Zones (Desktop) */}
          {!mobile && (
            <>
              <button
                onClick={goToPrevPage}
                disabled={currentPage <= 1}
                className="absolute left-0 top-0 bottom-0 w-20 flex items-center justify-start pl-4 opacity-0 hover:opacity-100 transition-opacity disabled:cursor-not-allowed group"
                aria-label="Previous page"
              >
                <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center group-hover:bg-black/70 transition-colors">
                  <ChevronLeft className="w-6 h-6 text-white" />
                </div>
              </button>
              <button
                onClick={goToNextPage}
                disabled={currentPage >= guideInfo?.total_pages}
                className="absolute right-0 top-0 bottom-0 w-20 flex items-center justify-end pr-4 opacity-0 hover:opacity-100 transition-opacity disabled:cursor-not-allowed group"
                aria-label="Next page"
              >
                <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center group-hover:bg-black/70 transition-colors">
                  <ChevronRight className="w-6 h-6 text-white" />
                </div>
              </button>
            </>
          )}
        </div>

        {/* Bottom Navigation */}
        <AnimatePresence>
          {(showControls || !mobile) && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className={`${mobile ? 'fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-lg border-t border-[#1a1a1a] px-4 py-4' : 'mt-6'}`}
            >
              <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
                {/* Previous Button */}
                <Button
                  onClick={goToPrevPage}
                  disabled={currentPage <= 1}
                  variant="outline"
                  className="border-[#333] text-white hover:bg-[#1a1a1a] disabled:opacity-30 rounded-full px-4 md:px-6"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Prev</span>
                </Button>

                {/* Page Indicator - Clickable to open page input */}
                <div className="flex-1 flex flex-col items-center">
                  {showPageInput ? (
                    <form 
                      onSubmit={(e) => {
                        e.preventDefault();
                        const num = parseInt(pageInputValue, 10);
                        if (num >= 1 && num <= guideInfo?.total_pages) {
                          setPageDirection(num > currentPage ? 1 : -1);
                          setCurrentPage(num);
                        }
                        setShowPageInput(false);
                        setPageInputValue('');
                      }}
                      className="flex items-center gap-2"
                    >
                      <input
                        ref={pageInputRef}
                        type="number"
                        min="1"
                        max={guideInfo?.total_pages}
                        value={pageInputValue}
                        onChange={(e) => setPageInputValue(e.target.value)}
                        onBlur={() => {
                          setTimeout(() => {
                            setShowPageInput(false);
                            setPageInputValue('');
                          }, 200);
                        }}
                        placeholder={currentPage.toString()}
                        className="w-16 bg-[#1a1a1a] border border-[#333] rounded px-2 py-1 text-white text-center text-sm focus:outline-none focus:border-[#D4AF37]"
                        autoFocus
                      />
                      <span className="text-[#666]">/ {guideInfo?.total_pages}</span>
                    </form>
                  ) : (
                    <button 
                      onClick={() => {
                        setShowPageInput(true);
                        setPageInputValue('');
                        setTimeout(() => pageInputRef.current?.focus(), 50);
                      }}
                      className="flex items-center gap-2 hover:bg-[#1a1a1a] px-3 py-1 rounded-lg transition-colors group"
                      title="Click to go to specific page"
                    >
                      <span className="text-white font-medium group-hover:text-[#D4AF37]">{currentPage}</span>
                      <span className="text-[#666]">/</span>
                      <span className="text-[#666]">{guideInfo?.total_pages}</span>
                    </button>
                  )}
                  {mobile && !showPageInput && (
                    <p className="text-[#666] text-xs mt-1">
                      {zoom > 1 ? 'Drag to pan • Double-tap to reset' : 'Tap page number to jump'}
                    </p>
                  )}
                </div>

                {/* Next Button */}
                <Button
                  onClick={goToNextPage}
                  disabled={currentPage >= guideInfo?.total_pages}
                  className="bg-[#D4AF37] hover:bg-[#C9A030] text-black disabled:opacity-30 rounded-full px-4 md:px-6"
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              
              {/* Desktop instructions */}
              {!mobile && (
                <p className="text-[#666] text-xs text-center mt-4">
                  {zoom > 1 ? 'Drag to pan • Double-click to reset' : 'Use arrow keys to navigate • Double-click to zoom'}
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile tap hint - shows briefly */}
        {mobile && !showControls && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-black/70 px-4 py-2 rounded-full"
          >
            <p className="text-[#999] text-xs">Tap to show controls</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
